import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  approveApplication,
  declineApplication,
  subscribeApplications,
} from "../../services/applicationsService";
import {
  FOUNDING_SLOT_CAP,
  countFoundingApproved,
  foundingSlotsRemaining,
  getRateIfApproved,
} from "../../services/feeLadder";
import { formatCount } from "../../pages/creator/creatorHelpers";
import AdminLayout from "./AdminLayout";
import "./AdminCreatorApprovals.css";

const BATCH_TARGET = 12;
const FILTERS = ["pending", "caution", "approved", "declined", "all"];
const FILTER_LABELS = {
  pending: "Pending",
  caution: "Caution",
  approved: "Approved",
  declined: "Declined",
  all: "All",
};

function isCaution(app) {
  return app.status === "pending" && app.aiFitCheck?.flag === "caution";
}

function provinceFromPostal(postalCode) {
  if (!postalCode) return "—";
  const first = postalCode.trim().charAt(0).toUpperCase();
  const map = {
    A: "NL", B: "NS", C: "PE", E: "NB",
    G: "QC", H: "QC", J: "QC",
    K: "ON", L: "ON", M: "ON", N: "ON", P: "ON",
    R: "MB", S: "SK", T: "AB", V: "BC",
    X: "NT/NU", Y: "YT",
  };
  return map[first] || first;
}

function formatApplied(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function getAiFlag(app) {
  // If AI fit-check failed/timed out, show no flag — never a hard dependency
  return app.aiFitCheck?.flag || null;
}

function AiFlagBadge({ flag }) {
  if (!flag) {
    return <span className="admin-muted-cell">—</span>;
  }
  if (flag === "caution") {
    return (
      <span className="admin-ai-flag caution">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        Caution
      </span>
    );
  }
  if (flag === "review") {
    return (
      <span className="admin-ai-flag review">
        Review
      </span>
    );
  }
  return (
    <span className="admin-ai-flag clear">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="20 6 9 17 4 12" />
      </svg>
      Clear
    </span>
  );
}

function getNicheSkewNotice(selectedApps) {
  if (selectedApps.length < 3) return null;
  const counts = {};
  selectedApps.forEach((app) => {
    const niche = app.niche || "Unknown";
    counts[niche] = (counts[niche] || 0) + 1;
  });
  const total = selectedApps.length;
  for (const [niche, count] of Object.entries(counts)) {
    if (count >= 3 && count / total >= 0.4) {
      return `${count} of ${total} selected are ${niche} — consider balancing the batch.`;
    }
  }
  return null;
}

function getBulkHint(selectedApps) {
  if (!selectedApps.length) return "";
  const allClear = selectedApps.every((app) => getAiFlag(app) === "clear");
  if (allClear) return "all AI-flagged Clear";
  const cautionCount = selectedApps.filter((app) => getAiFlag(app) === "caution").length;
  if (cautionCount) return `${cautionCount} flagged Caution — review before bulk approve`;
  return "mixed AI flags";
}

function RatePill({ app, slotsRemaining }) {
  if (app.status === "approved") {
    const tier = app.feeTier || (app.feeRate === 0.1 ? "founding" : "earlywave");
    if (tier === "founding") {
      return <span className="admin-rate-pill founding">✦ 10% forever</span>;
    }
    return <span className="admin-rate-pill earlywave">Early Wave · 4mo</span>;
  }
  const rate = getRateIfApproved({ foundingSlotsRemaining: slotsRemaining });
  return (
    <span className={`admin-rate-pill ${rate.type === "founding" ? "founding" : "earlywave"}`}>
      {rate.label}
    </span>
  );
}

function ApplicantRow({
  app,
  expanded,
  selected,
  slotsRemaining,
  onToggleExpand,
  onToggleSelect,
  onApprove,
  onDecline,
  busy,
}) {
  const initial = (app.name || app.handle || "?").charAt(0).toUpperCase();
  const flag = getAiFlag(app);
  const contentUrl = app.contentLink || "";

  return (
    <>
      <tr className={expanded ? "admin-expanded-row" : ""}>
        <td>
          <input
            type="checkbox"
            className="admin-row-checkbox"
            checked={selected}
            onChange={() => onToggleSelect(app.id)}
            disabled={app.status !== "pending"}
          />
        </td>
        <td className="admin-expand-cell" onClick={() => onToggleExpand(app.id)}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {expanded ? <path d="m6 9 6 6 6-6" /> : <path d="m9 6 6 6-6 6" />}
          </svg>
        </td>
        <td>
          <div className="admin-applicant-cell">
            <div className="admin-mini-avatar">{initial}</div>
            <div>
              <div className="admin-applicant-name">
                {app.name || app.handle}
                {contentUrl && (
                  <a href={contentUrl} target="_blank" rel="noreferrer" className="admin-social-link" title="Open content link">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="1" />
                    </svg>
                  </a>
                )}
              </div>
              <div className="admin-applicant-handle">@{app.handle}</div>
            </div>
          </div>
        </td>
        <td><span className="admin-niche-pill">{app.niche || "—"}</span></td>
        <td className="admin-muted-cell">{provinceFromPostal(app.postalCode)}</td>
        <td>{app.followers ? formatCount(app.followers) : app.fansReferred ? `${app.fansReferred} fans` : "—"}</td>
        <td>{app.engagementRate ? `${app.engagementRate}%` : "—"}</td>
        <td className="admin-date-cell">{formatApplied(app.appliedAt)}</td>
        <td>
          <AiFlagBadge flag={flag} />
          {app.aiFitCheck?.summary && (
            <div className="admin-ai-summary">{app.aiFitCheck.summary}</div>
          )}
          {app.aiFitCheck?.reasons?.length > 0 && (
            <div className="admin-reason-tags">
              {app.aiFitCheck.reasons.map((reason) => (
                <span key={reason} className="admin-reason-tag">{reason}</span>
              ))}
            </div>
          )}
        </td>
        <td><RatePill app={app} slotsRemaining={slotsRemaining} /></td>
        <td>
          <div className="admin-action-cell">
            <button
              type="button"
              className="admin-icon-btn approve"
              onClick={() => onApprove(app.id)}
              disabled={busy || app.status !== "pending"}
              title="Approve"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
            <button
              type="button"
              className="admin-icon-btn reject"
              onClick={() => onDecline(app.id)}
              disabled={busy || app.status !== "pending"}
              title="Decline"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="admin-detail-row">
          <td colSpan={2} />
          <td colSpan={9}>
            <div className="admin-detail-panel">
              <div className="admin-detail-item">
                <div className="admin-detail-label">Email</div>
                <div className="admin-detail-value">{app.email || "—"}</div>
              </div>
              <div className="admin-detail-item">
                <div className="admin-detail-label">Referred By</div>
                <div className="admin-detail-value">
                  {app.referredBy || app.referralCode ? app.referredBy || app.referralCode : "— Direct application"}
                </div>
              </div>
              <div className="admin-detail-item" style={{ flex: 1 }}>
                <div className="admin-detail-label">Content Type</div>
                <div className="admin-detail-value">
                  {app.oneSentence || app.contentLink || "—"}
                </div>
              </div>
              {app.rubricScore && (
                <div className="admin-detail-item" style={{ flex: 1 }}>
                  <div className="admin-detail-label">
                    Rubric Score{typeof app.rubricScore.total === "number" ? ` · ${app.rubricScore.total}/25` : ""}
                  </div>
                  <div className="admin-detail-value">
                    {Array.isArray(app.rubricScore.breakdown)
                      ? app.rubricScore.breakdown.map((row) => (
                          <div key={row.criterion}>
                            {row.criterion}: {row.score}
                            {row.note ? ` — ${row.note}` : ""}
                          </div>
                        ))
                      : String(app.rubricScore.total ?? app.rubricScore)}
                    <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-muted)" }}>
                      Informational only — under 15 does not block Approve.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminCreatorApprovals() {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("caution");
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [mobileExpandedId, setMobileExpandedId] = useState(null);

  useEffect(() => {
    return subscribeApplications(setApplications);
  }, []);

  const stats = useMemo(() => {
    const pending = applications.filter((a) => a.status === "pending").length;
    const caution = applications.filter(isCaution).length;
    const approved = applications.filter((a) => a.status === "approved").length;
    const foundingLeft = foundingSlotsRemaining(applications);
    return { pending, caution, approved, foundingLeft, foundingUsed: countFoundingApproved(applications) };
  }, [applications]);

  const batchProgress =
    stats.approved === 0
      ? 0
      : stats.approved % BATCH_TARGET === 0
        ? BATCH_TARGET
        : stats.approved % BATCH_TARGET;
  const batchRemaining = BATCH_TARGET - batchProgress;

  const filteredApps = useMemo(() => {
    let list = [...applications];

    if (filter === "pending") list = list.filter((a) => a.status === "pending");
    else if (filter === "caution") list = list.filter(isCaution);
    else if (filter === "approved") list = list.filter((a) => a.status === "approved");
    else if (filter === "declined") list = list.filter((a) => a.status === "declined");

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          (a.name || "").toLowerCase().includes(q) ||
          (a.handle || "").toLowerCase().includes(q) ||
          (a.niche || "").toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      if (sort === "caution") {
        const ac = isCaution(a) ? 0 : 1;
        const bc = isCaution(b) ? 0 : 1;
        if (ac !== bc) return ac - bc;
      }
      if (sort === "engagement") {
        return (Number(b.engagementRate) || 0) - (Number(a.engagementRate) || 0);
      }
      const aTime = a.appliedAt?.seconds || 0;
      const bTime = b.appliedAt?.seconds || 0;
      return bTime - aTime;
    });

    return list;
  }, [applications, filter, search, sort]);

  const selectedApps = useMemo(
    () => applications.filter((a) => selectedIds.has(a.id)),
    [applications, selectedIds]
  );

  const nicheNotice = getNicheSkewNotice(selectedApps);
  const slotsRemaining = stats.foundingLeft;

  const handleApprove = useCallback(async (id) => {
    setBusy(true);
    try {
      await approveApplication(id, currentUser?.uid);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Approve failed.");
    } finally {
      setBusy(false);
    }
  }, [currentUser?.uid]);

  const handleDecline = useCallback(async (id) => {
    setBusy(true);
    try {
      await declineApplication(id, currentUser?.uid);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Decline failed.");
    } finally {
      setBusy(false);
    }
  }, [currentUser?.uid]);

  const handleBulkApprove = async () => {
    const ids = [...selectedIds].filter((id) => {
      const app = applications.find((a) => a.id === id);
      return app?.status === "pending";
    });
    if (!ids.length) return;
    setBusy(true);
    try {
      for (const id of ids) {
        await approveApplication(id, currentUser?.uid);
      }
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      alert(err.message || "Bulk approve failed.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const statBlock = (
    <>
      <div className="admin-stat">
        <div className="admin-stat-num">{stats.pending}</div>
        <div className="admin-stat-label">Pending</div>
      </div>
      <div className="admin-stat">
        <div className="admin-stat-num red">{stats.caution}</div>
        <div className="admin-stat-label">Caution</div>
      </div>
      <div className="admin-stat">
        <div className="admin-stat-num gold">
          {stats.foundingLeft}/{FOUNDING_SLOT_CAP}
        </div>
        <div className="admin-stat-label">Founding Left</div>
      </div>
      <div className="admin-stat">
        <div className="admin-stat-num">{stats.approved}</div>
        <div className="admin-stat-label">Approved</div>
      </div>
    </>
  );

  return (
    <AdminLayout>
      <div className="admin-shell">
        <div className="admin-header-row">
          <div className="admin-page-header">
            <div className="admin-eyebrow">Creator Pipeline</div>
            <div className="admin-page-title">Creator <em>Approvals</em></div>
            <span className="admin-page-hand">— review applications and manage the founding roster</span>
          </div>
          <div className="admin-stat-strip">{statBlock}</div>
        </div>

        <div className="admin-batch-target-card">
          <div>
            <div className="admin-batch-target-label">✦ Next Batch Target</div>
            <div className="admin-batch-target-value">
              {batchProgress} of {BATCH_TARGET} approved{" "}
              <span>
                — {batchRemaining} more fills this batch
              </span>
            </div>
            {nicheNotice && (
              <p className="admin-niche-balance-notice" style={{ marginTop: 10, fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
                This batch leans heavily — {nicheNotice} Worth checking niche balance before finalizing.
              </p>
            )}
          </div>
          <div className="admin-batch-target-bar-wrap">
            <div
              className="admin-batch-target-bar"
              style={{ width: `${Math.min(100, (batchProgress / BATCH_TARGET) * 100)}%` }}
            />
          </div>
        </div>

        <div className="admin-toolbar">
          <div className="admin-filter-pills">
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`admin-pill${filter === key ? " active" : ""}${key === "caution" ? " caution-pill" : ""}`}
                onClick={() => setFilter(key)}
              >
                {key === "caution" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                )}
                {FILTER_LABELS[key]}
              </button>
            ))}
          </div>
          <div className="admin-toolbar-right">
            <select className="admin-sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="caution">Sort: Caution first</option>
              <option value="newest">Sort: Newest applied</option>
              <option value="engagement">Sort: Highest engagement</option>
            </select>
            <div className="admin-search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="search"
                placeholder="Search by name or niche"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="admin-bulk-bar">
            <div className="admin-bulk-bar-left">
              <span className="admin-bulk-count">{selectedIds.size} selected</span>
              <span className="admin-bulk-hint">{getBulkHint(selectedApps)}</span>
              {nicheNotice && <span className="admin-bulk-notice">{nicheNotice}</span>}
            </div>
            <div className="admin-bulk-bar-actions">
              <button type="button" className="admin-btn admin-btn-niche" onClick={handleBulkApprove} disabled={busy}>
                Approve Selected ({selectedIds.size})
              </button>
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setSelectedIds(new Set())}>
                Clear Selection
              </button>
            </div>
          </div>
        )}

        <div className="admin-kb-hint">
          Keyboard: <kbd>Y</kbd> approve <kbd>N</kbd> reject <kbd>↓</kbd> next row
        </div>

        <div className="admin-table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 20 }} />
                <th style={{ width: 24 }} />
                <th>Applicant</th>
                <th>Niche</th>
                <th>Location</th>
                <th>Followers</th>
                <th>Engagement</th>
                <th>Applied</th>
                <th>AI Fit Check</th>
                <th>Rate If Approved</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApps.length === 0 ? (
                <tr className="admin-empty-row">
                  <td colSpan={11}>No applications match this filter.</td>
                </tr>
              ) : (
                filteredApps.map((app) => (
                  <ApplicantRow
                    key={app.id}
                    app={app}
                    expanded={expandedId === app.id}
                    selected={selectedIds.has(app.id)}
                    slotsRemaining={slotsRemaining}
                    onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
                    onToggleSelect={toggleSelect}
                    onApprove={handleApprove}
                    onDecline={handleDecline}
                    busy={busy}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-mobile-cards">
          <div className="admin-m-stat-strip">{statBlock}</div>
          {filteredApps.map((app) => {
            const initial = (app.name || app.handle || "?").charAt(0).toUpperCase();
            const flag = getAiFlag(app);
            const mobileOpen = mobileExpandedId === app.id;
            return (
              <div key={app.id} className="admin-m-card">
                <div className="admin-m-card-top">
                  <div className="admin-m-avatar">{initial}</div>
                  <div>
                    <div className="admin-m-name-row">
                      <div className="admin-m-name">{app.name || app.handle}</div>
                    </div>
                    <div className="admin-m-handle">
                      @{app.handle} · {provinceFromPostal(app.postalCode)}
                    </div>
                  </div>
                </div>
                <div className="admin-m-meta-row">
                  <span className="admin-m-meta-chip">{app.niche}</span>
                  <span className="admin-m-meta-chip">{formatApplied(app.appliedAt)}</span>
                  <RatePill app={app} slotsRemaining={slotsRemaining} />
                </div>
                <div className="admin-m-ai-block">
                  <AiFlagBadge flag={flag} />
                  {app.aiFitCheck?.summary && (
                    <div className="admin-ai-summary" style={{ maxWidth: "none" }}>{app.aiFitCheck.summary}</div>
                  )}
                </div>
                {app.status === "pending" && (
                  <div className="admin-m-actions">
                    <button type="button" className="admin-btn admin-m-btn-approve" onClick={() => handleApprove(app.id)} disabled={busy}>
                      Approve
                    </button>
                    <button type="button" className="admin-btn admin-m-btn-reject" onClick={() => handleDecline(app.id)} disabled={busy}>
                      Not Yet
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="admin-m-detail-toggle"
                  onClick={() => setMobileExpandedId(mobileOpen ? null : app.id)}
                >
                  {mobileOpen ? "Hide details ▴" : "View email, referral & content details ▾"}
                </button>
                {mobileOpen && (
                  <div className="admin-detail-panel" style={{ marginLeft: 0, marginTop: 12 }}>
                    <div className="admin-detail-item">
                      <div className="admin-detail-label">Email</div>
                      <div className="admin-detail-value">{app.email}</div>
                    </div>
                    <div className="admin-detail-item">
                      <div className="admin-detail-label">Content</div>
                      <div className="admin-detail-value">{app.oneSentence || "—"}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
